import { useEffect, useMemo, useState } from "react";

import { listAllPermissions } from "@/modules/permissions";
import type { Permission } from "@/modules/permissions";

export interface PermissionOption {
  value: string;
  label: string;
}

export interface UsePermissionOptionsResult {
  options: PermissionOption[];
  /**
   * Catálogo completo por trás das opções. Diferente de `options`, preserva o
   * código e a descrição de cada permissão, exigidos pela relação selecionável
   * dos pop-ups de perfil.
   */
  permissions: Permission[];
  isLoading: boolean;
  hasError: boolean;
}

/** O perfil grava e filtra por claim (`codigo`), não pelo id do catálogo. */
function toOption(permission: Permission): PermissionOption {
  return { value: permission.codigo, label: permission.nome || permission.codigo };
}

export function usePermissionOptions(): UsePermissionOptionsResult {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    listAllPermissions({ sort: { field: "nome", direction: "asc" }, signal: controller.signal })
      .then((collected) => {
        if (cancelled) return;
        setPermissions(collected);
        setHasError(false);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPermissions([]);
        setHasError(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const options = useMemo(() => permissions.map(toOption), [permissions]);

  return { options, permissions, isLoading, hasError };
}
